export type SpacingSize = "xs" | "sm" | "md" | "lg" | "xl";

export type SpacingPrefix =
  | "p"
  | "px"
  | "py"
  | "pt"
  | "pb"
  | "pl"
  | "pr"
  | "mx"
  | "my"
  | "mt"
  | "mb"
  | "ml"
  | "mr"
  | "gap"
  | "gap-x"
  | "gap-y";

export const spacingClassName = (prefix: SpacingPrefix, size: SpacingSize | "auto"): string =>
  `${prefix}-${size}`;

export const alignItemsMapping: Record<
  "start" | "end" | "center" | "stretch" | "baseline",
  string
> = {
  start: "items-start",
  end: "items-end",
  center: "items-center",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

export const justifyItemsMapping: Record<"start" | "end" | "center" | "stretch", string> = {
  start: "justify-items-start",
  end: "justify-items-end",
  center: "justify-items-center",
  stretch: "justify-items-stretch",
};

export const justifyContentMapping: Record<
  "start" | "end" | "center" | "space-between" | "space-around" | "space-evenly",
  string
> = {
  start: "justify-start",
  end: "justify-end",
  center: "justify-center",
  "space-between": "justify-between",
  "space-around": "justify-around",
  "space-evenly": "justify-evenly",
};

export const justifySelfMapping: Record<"start" | "end" | "center" | "stretch", string> = {
  start: "justify-self-start",
  end: "justify-self-end",
  center: "justify-self-center",
  stretch: "justify-self-stretch",
};

export const gridColsMapping: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
  7: "grid-cols-7",
  8: "grid-cols-8",
  9: "grid-cols-9",
  10: "grid-cols-10",
  11: "grid-cols-11",
  12: "grid-cols-12",
};

export const gridColsMappingSm: Record<number, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
  5: "sm:grid-cols-5",
  6: "sm:grid-cols-6",
  7: "sm:grid-cols-7",
  8: "sm:grid-cols-8",
  9: "sm:grid-cols-9",
  10: "sm:grid-cols-10",
  11: "sm:grid-cols-11",
  12: "sm:grid-cols-12",
};

export const gridColsMappingMd: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
  5: "md:grid-cols-5",
  6: "md:grid-cols-6",
  7: "md:grid-cols-7",
  8: "md:grid-cols-8",
  9: "md:grid-cols-9",
  10: "md:grid-cols-10",
  11: "md:grid-cols-11",
  12: "md:grid-cols-12",
};

export const gridColsMappingLg: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
  7: "lg:grid-cols-7",
  8: "lg:grid-cols-8",
  9: "lg:grid-cols-9",
  10: "lg:grid-cols-10",
  11: "lg:grid-cols-11",
  12: "lg:grid-cols-12",
};

export const gridColsMappingXl: Record<number, string> = {
  1: "xl:grid-cols-1",
  2: "xl:grid-cols-2",
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
  5: "xl:grid-cols-5",
  6: "xl:grid-cols-6",
  7: "xl:grid-cols-7",
  8: "xl:grid-cols-8",
  9: "xl:grid-cols-9",
  10: "xl:grid-cols-10",
  11: "xl:grid-cols-11",
  12: "xl:grid-cols-12",
};

export const gridColSpanMapping: Record<"full" | number, string> = {
  full: "col-span-full",
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
  10: "col-span-10",
  11: "col-span-11",
  12: "col-span-12",
};

export const gridColSpanMappingSm: Record<"full" | number, string> = {
  full: "sm:col-span-full",
  1: "sm:col-span-1",
  2: "sm:col-span-2",
  3: "sm:col-span-3",
  4: "sm:col-span-4",
  5: "sm:col-span-5",
  6: "sm:col-span-6",
  7: "sm:col-span-7",
  8: "sm:col-span-8",
  9: "sm:col-span-9",
  10: "sm:col-span-10",
  11: "sm:col-span-11",
  12: "sm:col-span-12",
};

export const gridColSpanMappingMd: Record<"full" | number, string> = {
  full: "md:col-span-full",
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

export const gridColSpanMappingLg: Record<"full" | number, string> = {
  full: "lg:col-span-full",
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  5: "lg:col-span-5",
  6: "lg:col-span-6",
  7: "lg:col-span-7",
  8: "lg:col-span-8",
  9: "lg:col-span-9",
  10: "lg:col-span-10",
  11: "lg:col-span-11",
  12: "lg:col-span-12",
};

export const gridColSpanMappingXl: Record<"full" | number, string> = {
  full: "xl:col-span-full",
  1: "xl:col-span-1",
  2: "xl:col-span-2",
  3: "xl:col-span-3",
  4: "xl:col-span-4",
  5: "xl:col-span-5",
  6: "xl:col-span-6",
  7: "xl:col-span-7",
  8: "xl:col-span-8",
  9: "xl:col-span-9",
  10: "xl:col-span-10",
  11: "xl:col-span-11",
  12: "xl:col-span-12",
};

export const gridColSpanMappingXxl: Record<"full" | number, string> = {
  full: "2xl:col-span-full",
  1: "2xl:col-span-1",
  2: "2xl:col-span-2",
  3: "2xl:col-span-3",
  4: "2xl:col-span-4",
  5: "2xl:col-span-5",
  6: "2xl:col-span-6",
  7: "2xl:col-span-7",
  8: "2xl:col-span-8",
  9: "2xl:col-span-9",
  10: "2xl:col-span-10",
  11: "2xl:col-span-11",
  12: "2xl:col-span-12",
};

export const textSizeMapping = {
  normal: "text-base",
  xs: "text-xs",
  sm: "text-sm",
  lg: "text-lg",
  xl: "text-xl",
  h1: "text-h1",
  h2: "text-h2",
  h3: "text-h3",
  h4: "text-h4",
  h5: "text-h5",
  h6: "text-h6",
};

export const fontWeightMapping = {
  normal: "font-normal",
  bold: "font-semibold",
};

export type MinHeight = "sm" | "md" | "lg";

export const minHeightClassName = (minHeight: MinHeight): string => {
  switch (minHeight) {
    case "sm":
      return "min-h-8";
    case "md":
      return "min-h-12";
    case "lg":
      return "min-h-16";
  }
};

export type MaxWidth = "prose" | "document" | "sm" | "md" | "lg" | "xl" | "2xl";

export const maxWidthClassName = (maxWidth: MaxWidth): string => {
  switch (maxWidth) {
    case "prose":
      return "max-w-prose";
    case "document":
      return "max-w-[900px]";
    case "sm":
      return "max-w-(--breakpoint-sm)";
    case "md":
      return "max-w-(--breakpoint-md)";
    case "lg":
      return "max-w-(--breakpoint-lg)";
    case "xl":
      return "max-w-(--breakpoint-xl)";
    case "2xl":
      return "max-w-(--breakpoint-2xl)";
  }
};

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export const containerClassName = (v: true | Breakpoint) => {
  switch (v) {
    case "sm":
      return "sm:container";
    case "md":
      return "md:container";
    case "lg":
      return "lg:container";
    case "xl":
      return "xl:container";
    case "2xl":
      return "2xl:container";
    default:
      return "container";
  }
};
